import Link from 'next/link';
import * as React from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { colors, P, Headline, layout, darkColors } from '../common/styleguide';
import CustomAppearanceContext from '../context/CustomAppearanceContext';
import { Query } from '../types';
import urlWithQuery from '../util/urlWithQuery';
import { Button } from './Button';
import { CheckBox } from './CheckBox';
import { Filter as FilterIcon } from './Icons';

type FiltersProps = {
  query: Query;
};

type FilterButtonProps = {
  query: Query;
  onPress: () => void;
  isFilterVisible: boolean;
};

const uiFrameworks = [
  {
    param: 'angular',
    title: 'Angular',
  },
  {
    param: 'reactnative',
    title: 'React Native',
  },
  {
    param: 'react',
    title: 'React',
  },
  {
    param: 'vue',
    title: 'Vue',
  },
];
const supports = [
  {
    param: 'uiComponents',
    title: 'UI Components',
  },
  {
    param: 'templates',
    title: 'Templates',
  },
  {
    param: 'scss',
    title: 'SCSS',
  },
  {
    param: 'less',
    title: 'LESS',
  },
  {
    param: 'vanilacss',
    title: 'Vanila CSS',
  },
];

function ToggleLink({ query, paramName, title }) {
  let isSelected = !!query[paramName];

  return (
    <Link
      href={urlWithQuery('/', {
        ...query,
        [paramName]: !isSelected,
        offset: null,
      })}>
      <View style={styles.link}>
        <CheckBox value={isSelected} color={colors.primaryDark} />
        <P style={{ fontSize: 14 }}>{title}</P>
      </View>
    </Link>
  );
}

export const FilterButton = (props: FilterButtonProps) => {
  const { isFilterVisible, query, onPress } = props;
  const params = [
    ...supports.map((platform) => platform.param),
    ...uiFrameworks.map((platform) => platform.param),
    'hasExample',
    'hasImage',
    'hasTypes',
    'isMaintained',
    'isPopular',
    'isRecommended',
    'wasRecentlyUpdated',
  ];
  const filterCount = Object.keys(query).reduce(
    (acc, q) => (params.includes(q) ? acc + 1 : acc),
    0
  );

  return (
    <CustomAppearanceContext.Consumer>
      {(context) => (
        <Button
          onPress={onPress}
          style={[
            styles.button,
            { backgroundColor: context.isDark ? darkColors.border : colors.gray5 },
            isFilterVisible && styles.activeButton,
          ]}>
          <View style={styles.displayHorizontal}>
            <View style={styles.iconContainer}>
              <FilterIcon
                fill={isFilterVisible ? colors.gray7 : colors.white}
                width={14}
                height={12}
              />
            </View>
            <P style={[styles.buttonText, isFilterVisible && styles.activeButtonText]}>
              Filters{filterCount > 0 ? `: ${filterCount}` : ''}
            </P>
          </View>
        </Button>
      )}
    </CustomAppearanceContext.Consumer>
  );
};

export const Filters = (props: FiltersProps) => {
  const { query } = props;
  return (
    <CustomAppearanceContext.Consumer>
      {(context) => (
        <View
          style={[
            styles.wrapper,
            {
              backgroundColor: context.isDark ? darkColors.veryDark : colors.gray1,
            },
          ]}>
          <View style={styles.container}>
            <Headline style={styles.title}>UI Frameworks</Headline>
            <View style={styles.optionsContainer}>
              {uiFrameworks.map((platform) => (
                <ToggleLink
                  key={platform.param}
                  query={query}
                  paramName={platform.param}
                  title={platform.title}
                />
              ))}
            </View>
          </View>
          <View style={styles.container}>
            <Headline style={styles.title}>Supports</Headline>
            <View style={styles.optionsContainer}>
              {supports.map((platform) => (
                <ToggleLink
                  key={platform.param}
                  query={query}
                  paramName={platform.param}
                  title={platform.title}
                />
              ))}
            </View>
          </View>
          <View style={styles.container}>
            <Headline style={styles.title}>Status</Headline>
            <View style={styles.optionsContainer}>
              <ToggleLink
                key="hasExample"
                query={query}
                paramName="hasExample"
                title="Has example"
              />
              <ToggleLink
                key="hasImage"
                query={query}
                paramName="hasImage"
                title="Has image preview"
              />
              <ToggleLink
                key="hasTypes"
                query={query}
                paramName="hasTypes"
                title="Has TypeScript types"
              />
              <ToggleLink
                key="isMaintained"
                query={query}
                paramName="isMaintained"
                title="Maintained"
              />
              <ToggleLink key="isPopular" query={query} paramName="isPopular" title="Popular" />
              <ToggleLink
                key="wasRecentlyUpdated"
                query={query}
                paramName="wasRecentlyUpdated"
                title="Recently updated"
              />
              <ToggleLink
                key="isRecommended"
                query={query}
                paramName="isRecommended"
                title="Recommended"
              />
            </View>
          </View>
        </View>
      )}
    </CustomAppearanceContext.Consumer>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    paddingVertical: 8,
    flex: 1,
    flexGrow: 0,
    alignItems: 'center',
  },
  container: {
    width: '100%',
    paddingHorizontal: 16,
    paddingVertical: 8,
    maxWidth: layout.maxWidth,
  },
  optionsContainer: {
    flexWrap: 'wrap',
    flexDirection: 'row',
  },
  title: {
    marginBottom: 8,
  },
  link: {
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
    marginRight: 16,
    marginVertical: 4,
    alignItems: 'center',
    flexDirection: 'row',
  },
  button: {
    height: 24,
    paddingHorizontal: 8,
  },
  activeButton: {
    backgroundColor: colors.primary,
  },
  buttonText: {
    fontSize: 14,
    color: colors.white,
    marginLeft: 6,
    fontWeight: '500',
  },
  activeButtonText: {
    color: colors.gray7,
  },
  iconContainer: {
    top: 1,
  },
  displayHorizontal: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
